import { getEnabledElement } from '@cornerstonejs/core';
import type { Types as CoreTypes } from '@cornerstonejs/core';
import {
  annotation as csAnnotation,
  ToolGroupManager,
} from '@cornerstonejs/tools';

/** Cornerstone's hover distance, so a click reaches as far as hover does. */
const PROXIMITY_PX = 6;

interface HitTestAnnotation {
  annotationUID?: string;
}

interface HitTestTool {
  isPointNearTool?: (
    element: HTMLDivElement,
    annotation: unknown,
    canvasCoords: [number, number],
    proximity: number,
    interactionType: string
  ) => boolean;
  filterInteractableAnnotationsForElement?: (
    element: HTMLDivElement,
    annotations: HitTestAnnotation[]
  ) => HitTestAnnotation[] | undefined;
}

/**
 * The visible annotation under a mouse event on the frame shown, by the tools'
 * own hit test. Cornerstone never sets the hover flag on a locked annotation
 * (a finalized report), so hover and click selection can't rely on that flag.
 */
export function annotationUIDAtEvent(
  event: MouseEvent,
  toolNames: readonly string[]
): string | null {
  const element = (event.target as Element | null)?.closest?.(
    '[data-viewport-uid]'
  ) as HTMLDivElement | null;
  if (!element) return null;
  const enabled = getEnabledElement(element);
  if (!enabled) return null;
  const toolGroup = ToolGroupManager.getToolGroupForViewport(
    enabled.viewportId,
    enabled.renderingEngineId
  );
  if (!toolGroup) return null;

  const rect = element.getBoundingClientRect();
  const point: [number, number] = [
    event.clientX - rect.left,
    event.clientY - rect.top,
  ];
  const hits = toolNames.map((toolName) => {
    const tool = toolGroup.getToolInstance(toolName) as HitTestTool | undefined;
    if (!tool?.isPointNearTool) return null;
    const annotations = (csAnnotation.state.getAnnotations(toolName, element) ??
      []) as HitTestAnnotation[];
    // Cornerstone's own filter (frame shown, visible), so a video frame's
    // annotations match however their referencedImageId is spelled.
    const shown =
      tool.filterInteractableAnnotationsForElement?.(element, annotations) ??
      [];
    // Drawn last is on top, so test it first.
    const hit = [...shown]
      .reverse()
      .find(
        (ann) =>
          !!ann.annotationUID &&
          !!tool.isPointNearTool?.(element, ann, point, PROXIMITY_PX, 'mouse')
      );
    return hit?.annotationUID ?? null;
  });
  return hits.find((uid) => uid !== null) ?? null;
}

interface HandlesLike {
  data: {
    handles: { points?: CoreTypes.Point3[]; activeHandleIndex?: number | null };
  };
}

/** The point a press lands on, checked before the label so a point under it stays grabbable. */
export function pointNearCanvas(
  element: HTMLDivElement,
  annotation: HandlesLike,
  canvasCoords: CoreTypes.Point2,
  proximity: number
): CoreTypes.Point3 | undefined {
  const viewport = getEnabledElement(element)?.viewport;
  const points = annotation.data.handles.points ?? [];
  if (!viewport) return undefined;
  const i = points.findIndex((p) => {
    const [x, y] = viewport.worldToCanvas(p);
    return Math.hypot(x - canvasCoords[0], y - canvasCoords[1]) < proximity;
  });
  if (i === -1) return undefined;
  // eslint-disable-next-line no-param-reassign
  annotation.data.handles.activeHandleIndex = i;
  return points[i];
}
